/**
 * Onboarding API (RTK Query)
 * Manages onboarding status, questions, and answer submission
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { apiClientBaseQuery } from '@/store/api/apiClientBaseQuery';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface OnboardingStatus {
    answered_count: number;
    answers: Record<string, string | string[] | null>;
    bonus_tokens_earned: number;
    total_questions: number;
}

export interface OnboardingOption {
    key: string;
    label: string;
}

export interface OnboardingQuestion {
    id: string;
    is_multi_select: boolean;
    key: string;
    options: OnboardingOption[];
    question_text: string;
    sort_order: number;
}

export interface OnboardingQuestionsResponse {
    questions: OnboardingQuestion[];
}

export interface AnswerRequest {
    question_key: string;
    answer: string | string[];
}

export interface AnswerResponse {
    answer: string | string[];
    created_at: string;
    question_key: string;
}

// ─── API ───────────────────────────────────────────────────────────────────────

export const onboardingApi = createApi({
    reducerPath: 'onboardingApi',
    baseQuery: apiClientBaseQuery,
    tagTypes: ['OnboardingStatus', 'OnboardingQuestions'],
    endpoints: (builder) => ({
        getOnboardingStatus: builder.query<OnboardingStatus, void>({
            query: () => ({ url: 'onboarding/status' }),
            providesTags: ['OnboardingStatus'],
        }),

        getOnboardingQuestions: builder.query<OnboardingQuestion[], void>({
            query: () => ({ url: 'onboarding/questions' }),
            transformResponse: (res: OnboardingQuestionsResponse) => res.questions,
            providesTags: ['OnboardingQuestions'],
        }),

        submitOnboardingAnswer: builder.mutation<AnswerResponse, AnswerRequest>({
            query: (body) => ({
                url: 'onboarding/answer',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['OnboardingStatus'],
        }),
    }),
});

export const {
    useGetOnboardingStatusQuery,
    useGetOnboardingQuestionsQuery,
    useSubmitOnboardingAnswerMutation,
} = onboardingApi;
